import {shallow} from "zustand/vanilla/shallow";
import {VisionBoardEdgeId, VisionBoardNodeId} from "../../cdx-models/utils/extended-fields";
import {MutableRefObject, useRef, useSyncExternalStore} from "react";
import {MiniEvent} from "@cdx/common";

type SelectedEdge = {id: VisionBoardEdgeId; mode: "select" | "edit"};

export type SelectionStore = ReturnType<typeof createCanvasSelectionStore>;

export const createCanvasSelectionStore = (onUpdate: (set: Set<VisionBoardNodeId>) => void) => {
  let selectedNodeIdSet = new Set<VisionBoardNodeId>();

  let lastSetNodeIdArg: VisionBoardNodeId[] = [];
  const nodeEvents = new MiniEvent<void>();

  const afterUpdateNodeIds = (explicitSet: VisionBoardNodeId[] = []) => {
    lastSetNodeIdArg = explicitSet;
    nodeEvents.emit();
    onUpdate(selectedNodeIdSet);
  };

  const resetNodeSelection = () => {
    if (selectedNodeIdSet.size > 0) {
      selectedNodeIdSet.clear();
      afterUpdateNodeIds();
    }
  };

  let selectedEdge: null | SelectedEdge = null;
  const edgeEvents = new MiniEvent<void>();

  const resetEdgeSelection = () => {
    if (selectedEdge) {
      selectedEdge = null;
      edgeEvents.emit();
    }
  };

  return {
    useSelectedNodes: <T>(selectFn: (set: Set<VisionBoardNodeId>) => T): T => {
      const prev = useRef<T>();
      return useSyncExternalStore(nodeEvents.addListener, () => {
        const next = selectFn(selectedNodeIdSet);
        return shallow(prev.current, next) ? (prev.current as T) : (prev.current = next);
      });
    },
    getSelectedNodeIdsSnapshot: () => selectedNodeIdSet,

    setNodeIds: (ids: VisionBoardNodeId[]) => {
      if (roughlyEqual(lastSetNodeIdArg, ids)) return;
      selectedNodeIdSet = new Set(ids);
      afterUpdateNodeIds(ids);
      resetEdgeSelection();
    },
    addNodeId: (id: VisionBoardNodeId) => {
      selectedNodeIdSet.add(id);
      afterUpdateNodeIds();
      resetEdgeSelection();
    },
    removeNodeId: (id: VisionBoardNodeId) => {
      selectedNodeIdSet.delete(id);
      afterUpdateNodeIds();
      resetEdgeSelection();
    },

    useSelectedEdge: <T>(selectFn: (edge: SelectedEdge | null) => T) => {
      const prev = useRef<T>();
      return useSyncExternalStore(edgeEvents.addListener, () => {
        const next = selectFn(selectedEdge);
        return shallow(prev.current, next) ? (prev.current as T) : (prev.current = next);
      });
    },

    setEdgeId: (id: VisionBoardEdgeId, mode: "select" | "edit") => {
      selectedEdge = {id, mode};
      edgeEvents.emit();
      resetNodeSelection();
    },

    clear: () => {
      const needsReset = selectedNodeIdSet.size > 0 || selectedEdge;
      if (!needsReset) return false;
      resetNodeSelection();
      resetEdgeSelection();
      return true;
    },
  };
};
const roughlyEqual = <T>(a: T[], b: T[]) => {
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  if (a[0] !== b[0]) return false;
  const lastIdx = a.length - 1;
  if (a[lastIdx] !== b[lastIdx]) return false;
  return true;
};

const canvasNodeRefMap = new Map<VisionBoardNodeId, MutableRefObject<HTMLDivElement | null>>();

export const registerCanvasNode = (
  id: VisionBoardNodeId,
  ref: MutableRefObject<HTMLDivElement | null>
) => {
  canvasNodeRefMap.set(id, ref);
  return () => {
    canvasNodeRefMap.delete(id);
  };
};

export const getCanvasNodeRefs = (ids: VisionBoardNodeId[]) => {
  return ids.map((id) => [id, canvasNodeRefMap.get(id)!] as const).filter((t) => t[1]);
};
export const getCanvasNodeRef = (id: VisionBoardNodeId) => {
  return canvasNodeRefMap.get(id);
};
